import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonPage,
    IonContent,
    IonCard,
    IonCardContent,
    IonLabel,
    IonItem,
    IonRadioGroup,
    IonRadio,
    IonButton,
    IonText,
    IonAlert,
    IonModal,
} from "@ionic/react";

// Question Interface
interface QuestionData {
    sentence: string;
    correctArticle: string;
    options: string[];
}


const Articles: React.FC = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const modal = useRef<HTMLIonModalElement>(null);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleSubmit = (option: React.SetStateAction<string | null>) => {
        setSelectedOption(option);
        const currentQuestion = questions[currentIndex];
        if (option === currentQuestion.correctArticle) {
            // Automatically move to the next question
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                }
                else {
                    setCurrentIndex(0);
                    fetchQuestions();
                }
            }, 5000); // Delay reduced to 3 seconds
        }
    };

    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'Articles.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/Articles.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Articles Worksheets for UKG | Learn A, An, The with Fun Activities"; // Set page title
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content='Explore UKG-level articles (a, an, the) at Mama’s Guidance through interactive worksheets that help children understand and apply correct usage in fun, simple sentences.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Articles – A, An, The</h1>

                                    <p>
                                        Articles are small but important words in the English language. With these <strong>UKG-level article worksheets</strong>, children will learn how to use <strong>‘a’, ‘an’, and ‘the’</strong> correctly in sentences. These fun and colorful worksheets help young learners recognize vowel and consonant sounds and apply articles properly.
                                    </p>

                                    <h2>📘 What Are Articles?</h2>
                                    <p>
                                        Articles are used before nouns to specify or introduce them:
                                        <ul>
                                            <li><strong>a</strong> – used before words that begin with a consonant sound (e.g., a dog, a pen)</li>
                                            <li><strong>an</strong> – used before words that begin with a vowel sound (e.g., an apple, an elephant)</li>
                                            <li><strong>the</strong> – used to refer to a specific or known object (e.g., the sun, the moon)</li>
                                        </ul>
                                    </p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Understand the difference between ‘a‘, ‘an‘, and ‘the‘</li>
                                        <li>Recognize vowel and consonant sounds</li>
                                        <li>Choose the correct article for a given word or picture</li>
                                        <li>Use articles correctly in simple sentences</li>
                                    </ul>

                                    <h2>📋 Activities Included</h2>
                                    <ul>
                                        <li>Fill in the blanks with the correct article</li>
                                        <li>Match objects with ‘a’ or ‘an’</li>
                                        <li>Circle the correct article for each word</li>
                                        <li>Use ‘the’ in proper context (e.g., the sun, the sky)</li>
                                        <li>Picture-based article identification exercises</li>
                                    </ul>

                                    <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                    <ul>
                                        <li>Start with ‘a’ and ‘an’ using picture cards or toys</li>
                                        <li>Emphasize vowel and consonant sounds when teaching articles</li>
                                        <li>Use short sentences to practice article placement</li>
                                        <li>Encourage reading aloud with emphasis on article usage</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/english-language/vowels-and-consonants"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Vowels and Consonants</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/english-language/one-and-many"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>One and Many</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/english-language/rhyming-words"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Rhyming Words</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Make article learning playful—turn it into a guessing game using toys or flashcards. Kids learn better when grammar feels like fun!
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Articles – A, An, The</h1>

                                <p>
                                    Articles are small but important words in the English language. With these <strong>UKG-level article worksheets</strong>, children will learn how to use <strong>‘a’, ‘an’, and ‘the’</strong> correctly in sentences. These fun and colorful worksheets help young learners recognize vowel and consonant sounds and apply articles properly.
                                </p>

                                <h2>📘 What Are Articles?</h2>
                                <p>
                                    Articles are used before nouns to specify or introduce them:
                                    <ul>
                                        <li><strong>a</strong> – used before words that begin with a consonant sound (e.g., a dog, a pen)</li>
                                        <li><strong>an</strong> – used before words that begin with a vowel sound (e.g., an apple, an elephant)</li>
                                        <li><strong>the</strong> – used to refer to a specific or known object (e.g., the sun, the moon)</li>
                                    </ul>
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Understand the difference between ‘a‘, ‘an‘, and ‘the‘</li>
                                    <li>Recognize vowel and consonant sounds</li>
                                    <li>Choose the correct article for a given word or picture</li>
                                    <li>Use articles correctly in simple sentences</li>
                                </ul>

                                <h2>📋 Activities Included</h2>
                                <ul>
                                    <li>Fill in the blanks with the correct article</li>
                                    <li>Match objects with ‘a’ or ‘an’</li>
                                    <li>Circle the correct article for each word</li>
                                    <li>Use ‘the’ in proper context (e.g., the sun, the sky)</li>
                                    <li>Picture-based article identification exercises</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                <ul>
                                    <li>Start with ‘a’ and ‘an’ using picture cards or toys</li>
                                    <li>Emphasize vowel and consonant sounds when teaching articles</li>
                                    <li>Use short sentences to practice article placement</li>
                                    <li>Encourage reading aloud with emphasis on article usage</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/english-language/vowels-and-consonants"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Vowels and Consonants</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/one-and-many"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>One and Many</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/rhyming-words"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Rhyming Words</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Make article learning playful—turn it into a guessing game using toys or flashcards. Kids learn better when grammar feels like fun!
                                </p>
                            </article>
                            <h4>Fill in the blank with the correct article:</h4>
                            <p>
                                <strong>Sentence:</strong> <IonText>{questions[currentIndex]?.sentence}</IonText>
                            </p>

                            <IonRadioGroup
                                value={selectedOption}
                                onIonChange={(e) => handleSubmit(e.detail.value)}
                            >
                                {questions[currentIndex]?.options.map((option, index) => (
                                    <IonItem
                                        className={` ${selectedOption === option
                                            ? option === questions[currentIndex]?.correctArticle
                                                ? "correct"
                                                : "errorItems"
                                            : ""
                                            }`}
                                        key={index}>
                                        <IonLabel>{option}</IonLabel>
                                        <IonRadio slot="start" value={option} />
                                    </IonItem>
                                ))}
                            </IonRadioGroup>
                        </>

                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default Articles;
